import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useReportFilters } from './hooks/useReportFilters';
import { useUserContext } from './hooks/useUserContext';
import { ReportFilters } from './components/ReportFilters';
import { StickyDateRangeHeader } from './components/StickyDateRangeHeader';
import { ReportsSummaryDashboard } from './components/ReportsSummaryDashboard';
import { ReportsTabs } from './components/ReportsTabs';
import { useReportsTabs } from './hooks/useReportsTabs';
import { RegionalManagerRoutine } from './components/RegionalManagerRoutine';

// Report sections
import { DailyReceivingSection } from './components/DailyReceivingSection';
import { PatientOrderTrackingSection } from './components/PatientOrderTrackingSection';
import { FrameTurnaroundSection } from './components/FrameTurnaroundSection';
import { OrderEfficiencySection } from './components/OrderEfficiencySection';
import { LocationSummarySection } from './components/LocationSummarySection';
import { ManufacturerPerformanceSection } from './components/ManufacturerPerformanceSection';
import { AgingExceptionsSection } from './components/AgingExceptionsSection';
import { UsageComplianceSection } from './components/UsageComplianceSection';

export function ReportsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { activeTab, setActiveTab } = useReportsTabs();
  const userContext = useUserContext();
  
  const {
    filters,
    setDatePreset,
    setDateFrom,
    setDateTo,
    setLocationIds,
    setManufacturerIds,
    resetFilters,
  } = useReportFilters();

  // Refs for scrolling to sections
  const usageRef = useRef<HTMLDivElement>(null);
  const activityRef = useRef<HTMLDivElement>(null);
  
  // Track if we've already scrolled to prevent re-scrolling
  const hasScrolledRef = useRef(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Track processed URL params to prevent re-processing
  const processedParamsRef = useRef<string>('');

  // State for highlight animation
  const [highlightSection, setHighlightSection] = useState<string | null>(null);

  /**
   * Apply URL params for location filtering and section scrolling
   * This enables navigation from Dashboard with pre-applied filters
   */
  useEffect(() => {
    const locationId = searchParams.get('locationId');
    const section = searchParams.get('section');
    const paramsKey = `${locationId || ''}-${section || ''}`;

    // Only process if params have actually changed
    if (paramsKey === processedParamsRef.current) {
      return; // Already processed these params, prevent infinite loop
    }

    // Mark these params as processed
    processedParamsRef.current = paramsKey;

    // Apply location filter if provided and different from current
    if (locationId) {
      const currentLocationIds = filters.locationIds;
      // Only update if locationId is not already in the list
      if (!currentLocationIds.includes(locationId)) {
        setLocationIds([locationId]);
      }
    }

    // Scroll to section if provided AND we haven't already scrolled
    // This prevents re-scrolling when data loads or component re-renders
    if (section && !hasScrolledRef.current) {
      // Mark that we're about to scroll (prevents re-scrolling)
      hasScrolledRef.current = true;

      // Apply highlight animation immediately
      setHighlightSection(section);
      setTimeout(() => setHighlightSection(null), 2000);

      const scrollToSection = (attempt = 0) => {
        // Maximum 20 attempts (10 seconds total) - give more time for data to load
        if (attempt > 20) {
          console.warn(`Could not find ${section} section after ${attempt} attempts`);
          hasScrolledRef.current = false; // Reset if we can't find the element
          return;
        }

        let targetRef: React.RefObject<HTMLDivElement> | null = null;

        if (section === 'usage') {
          targetRef = usageRef;
          setActiveTab('compliance'); // Switch to compliance tab
        } else if (section === 'activity') {
          targetRef = activityRef;
          setActiveTab('performance'); // Switch to performance tab
        }

        // Check if element exists and has a valid position
        if (targetRef?.current) {
          const element = targetRef.current;
          const rect = element.getBoundingClientRect();
          
          // Check if element is actually rendered (has dimensions)
          if (rect.height > 0 || rect.width > 0) {
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            const elementPosition = rect.top + scrollTop;
            const targetScrollPosition = Math.max(0, elementPosition - 20); // 20px offset from top

            // Use instant scroll - no animation to prevent interference
            window.scrollTo(0, targetScrollPosition);

            // Clear URL params after scroll - use replace to avoid adding to history
            // Only clear if section param still exists (hasn't been cleared already)
            const currentSection = searchParams.get('section');
            if (currentSection === section) {
              // Delay clearing params slightly to ensure scroll completed
              setTimeout(() => {
                setSearchParams((prev) => {
                  const newParams = new URLSearchParams(prev);
                  newParams.delete('section');
                  newParams.delete('locationId');
                  return newParams;
                }, { replace: true });
              }, 100);
            }
            return; // Successfully scrolled, exit
          }
        }

        // Element not ready yet, try again after a delay
        // Use shorter delays initially, longer as we retry
        const delay = attempt < 5 ? 200 : 500;
        scrollTimeoutRef.current = setTimeout(() => {
          scrollToSection(attempt + 1);
        }, delay);
      };

      // Clear any existing timeout
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      // Start trying to scroll - will retry if element not ready
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          scrollTimeoutRef.current = setTimeout(() => {
            scrollToSection(0);
          }, 100); // Shorter initial delay
        });
      });
    }

    // Cleanup: reset scroll flag when component unmounts or params change significantly
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
        scrollTimeoutRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, filters.locationIds, setActiveTab]); // Include filters.locationIds to check if update needed

  // Reset processed params ref when component unmounts or section param is removed
  useEffect(() => {
    const section = searchParams.get('section');
    if (!section) {
      // Section param removed - reset flags so we can scroll again if it comes back
      processedParamsRef.current = '';
      hasScrolledRef.current = false;
    }
  }, [searchParams]);

  // Auto-filter to single location for single-location users
  useEffect(() => {
    if (userContext.singleLocationId && filters.locationIds.length === 0) {
      setLocationIds([userContext.singleLocationId]);
    }
  }, [userContext.singleLocationId, filters.locationIds.length, setLocationIds]);

  // Build filter props for report sections
  const locationFilterIds = filters.locationIds.length > 0 ? filters.locationIds : undefined;
  const manufacturerFilterIds = filters.manufacturerIds.length > 0 ? filters.manufacturerIds : undefined;

  const baseFilters = {
    date_from: filters.dateFrom,
    date_to: filters.dateTo,
    location_ids: locationFilterIds,
  };

  // Check which reports to show based on active tab
  const showDailyOps = activeTab === 'daily' || activeTab === 'all';
  const showPerformance = activeTab === 'performance' || activeTab === 'all';
  const showCompliance = activeTab === 'compliance' || activeTab === 'all';

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Reports</h1>
        <p className="mt-2 text-sm sm:text-base text-gray-600">
          {userContext.isMultiLocation
            ? 'Compare locations, track performance, and monitor compliance'
            : 'Track your location\'s performance and compliance'
          }
        </p>
      </div>

      {/* Shared Filters */}
      <ReportFilters
        dateFrom={filters.dateFrom}
        dateTo={filters.dateTo}
        locationIds={filters.locationIds}
        manufacturerIds={filters.manufacturerIds}
        datePreset={filters.datePreset}
        onDatePresetChange={setDatePreset}
        onDateFromChange={setDateFrom}
        onDateToChange={setDateTo}
        onLocationIdsChange={setLocationIds}
        onManufacturerIdsChange={setManufacturerIds}
        onReset={resetFilters}
        showManufacturerFilter={false}
      />

      {/* Sticky Date Range Header */}
      <StickyDateRangeHeader
        dateFrom={filters.dateFrom}
        dateTo={filters.dateTo}
        datePreset={filters.datePreset}
        locationIds={filters.locationIds}
      />

      {/* Summary Dashboard */}
      <ReportsSummaryDashboard
        filters={baseFilters}
        isMultiLocation={userContext.isMultiLocation}
      />

      {/* Regional Manager Routine - only for multi-location users */}
      {userContext.isMultiLocation && (
        <div className="mb-6">
          <RegionalManagerRoutine />
        </div>
      )}

      {/* Tab Navigation */}
      <ReportsTabs
        activeTab={activeTab}
        onTabChange={setActiveTab}
        isMultiLocation={userContext.isMultiLocation}
      />

      {/* Report Sections based on active tab */}
      <div className="space-y-6">
        {/* Daily Operations Tab */}
        {showDailyOps && (
          <>
            {activeTab === 'all' && (
              <h2 className="text-lg font-semibold text-gray-900 pt-2 border-b pb-2">
                Daily Operations
              </h2>
            )}
            
            <DailyReceivingSection
              filters={{
                ...baseFilters,
              }}
            />

            <PatientOrderTrackingSection
              locationIds={locationFilterIds}
            />
          </>
        )}

        {/* Performance Tab */}
        {showPerformance && (
          <>
            {activeTab === 'all' && (
              <h2 className="text-lg font-semibold text-gray-900 pt-4 border-b pb-2">
                Performance
              </h2>
            )}

            {/* Location Summary */}
            <div
              ref={activityRef}
              className={`transition-all duration-500 ${
                highlightSection === 'activity' ? 'ring-4 ring-blue-400 ring-opacity-50 rounded-lg' : ''
              }`}
            >
              <LocationSummarySection filters={baseFilters} />
            </div>

            <ManufacturerPerformanceSection
              filters={{
                ...baseFilters,
                manufacturer_ids: manufacturerFilterIds,
              }}
            />

            <FrameTurnaroundSection
              filters={{
                ...baseFilters,
                order_type: 'ALL',
              }}
            />

            <OrderEfficiencySection
              filters={{
                ...baseFilters,
              }}
            />
          </>
        )}

        {/* Compliance Tab */}
        {showCompliance && (
          <>
            {activeTab === 'all' && (
              <h2 className="text-lg font-semibold text-gray-900 pt-4 border-b pb-2">
                Compliance & Exceptions
              </h2>
            )}

            <AgingExceptionsSection
              locationIds={locationFilterIds}
              manufacturerIds={manufacturerFilterIds}
            />

            {/* Usage & Compliance */}
            <div
              ref={usageRef}
              className={`transition-all duration-500 ${
                highlightSection === 'usage' ? 'ring-4 ring-blue-400 ring-opacity-50 rounded-lg' : ''
              }`}
            >
              <UsageComplianceSection filters={baseFilters} />
            </div>
          </>
        )}
      </div>

      {/* Help Text - collapsed by default */}
      <details className="mt-8">
        <summary className="cursor-pointer text-sm font-medium text-gray-700 hover:text-gray-900">
          Report Descriptions & Help
        </summary>
        <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <div className="space-y-4 text-sm text-gray-600">
            <div>
              <h4 className="font-semibold text-gray-800 mb-2">Daily Operations</h4>
              <ul className="space-y-1 ml-4 list-disc">
                <li><strong>Daily Receiving:</strong> Frames received in the selected date range, grouped by date, manufacturer, or location.</li>
                <li><strong>Patient Order Tracking:</strong> Track all patient-specific frame orders with current status.</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-gray-800 mb-2">Performance</h4>
              <ul className="space-y-1 ml-4 list-disc">
                <li><strong>Location Summary:</strong> Side-by-side comparison of frame activity across locations.</li>
                <li><strong>Manufacturer Performance:</strong> Ordering and receiving metrics per manufacturer.</li>
                <li><strong>Frame Turnaround:</strong> Average time from entry → ordered → received.</li>
                <li><strong>Order Efficiency:</strong> Whether orders meet minimum quantity requirements.</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-gray-800 mb-2">Compliance</h4>
              <ul className="space-y-1 ml-4 list-disc">
                <li><strong>Aging & Exceptions:</strong> Frames that have been pending or ordered for too long.</li>
                <li><strong>Usage & Compliance:</strong> System adoption tracking per location.</li>
              </ul>
            </div>
          </div>
        </div>
      </details>
    </div>
  );
}
